import { makeAutoObservable } from 'mobx';
import moment from 'moment';
import apiManage from '../../request';
import { RiskOrder, RiskEvent } from '../../models';
import { ConditionType } from '../../component/form/search-key-input.component';
import { dateFormat, formatRiskMoney, formatRiskMinutes, formatRiskStatusText, formatEventTypeText, validateCurrentDate } from '../../constants/tool-order';
import { exportMultipleSheetExcel } from '../../constants/tool.fn';
import { LOCAL_STORAGE_KEYS } from '../../modules/cache/local-storage.constant';
import LSM from '../../modules/cache/tool-local-storage';
import i18n from '../../i18n';
import GlobalNotification from '../../component/common/snackbar';
import { dateFormat as dateFormat2 } from '../../constants/utils';
import setLoading from '../../component/linear-determinate';
import { getRiskTagTextForOrder, getRiskTagsForOrder, normalizeInstantRiskResponse, RiskOrderGroup, RiskOrderMode } from './risk-order-mode';

/** 即时列表展示模式：卡片轮播 / 表格 */
export type RiskOrderListViewMode = 'card' | 'table';

/** 表格模式下展示的列表类型 */
export type RiskOrderListTableType = 'order' | 'event';

/**
 * 判断即时列表项是否为异常订单（与卡片、导出逻辑一致）
 */
export function isRiskOrderListItemOrder(item: RiskOrder | RiskEvent): item is RiskOrder {
    return (item as RiskOrder).order_head_id !== undefined;
}

class RiskOrderListState {
    // 加载状态
    loading = false;

    /** 接口返回的原始列表（未按风险标签筛选） */
    rawOrders: Array<RiskOrder | RiskEvent> = [];

    // 订单和事件列表（含风险标签筛选后的展示数据）
    orders: Array<RiskOrder | RiskEvent> = [];

    // 选中的订单
    selectedOrder: RiskOrder | null = null;
    selectedEvent: RiskEvent | null = null;

    // 弹窗状态
    orderDialogOpen = false;
    eventDialogOpen = false;

    /** 高级筛选条件（与云端列表 SearchKeyInput 一致；风险标签/订单号等仍在前端过滤） */
    conditions: ConditionType[] = [];

    /** 日期筛选起止（YYYY-MM-DD HH:mm），请求即时接口必填参数 start_time / end_time */
    startTime: string;
    endTime: string;

    // 当前餐厅标识
    stringId: string = '';
    // AI 分析对话框状态
    aiAnalysisDialogOpen = false;

    /** 展示模式：默认卡片 */
    viewMode: RiskOrderListViewMode = 'card';

    /** 表格模式下列表类型：异常订单 / 异常事件 */
    tableListType: RiskOrderListTableType = 'order';

    /** 即时接口数据模式：订单聚合或单个风险点。 */
    riskDataMode: RiskOrderMode = 'order';

    /** 表格前端分页当前页（0-based，与 TableComponent currentPage 一致） */
    tablePage = 0;

    /** 表格前端分页每页条数 */
    tablePerPage = 20;

    constructor() {
        makeAutoObservable(this);
        const { startTime, endTime } = validateCurrentDate();
        this.startTime = moment(startTime, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm');
        this.endTime = moment(endTime, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm');
    }

    // ==================== Setter 方法 ====================

    setLoading(value: boolean) {
        this.loading = value;
    }

    setOrders(value: Array<RiskOrder | RiskEvent>) {
        this.orders = value;
    }

    setRawOrders(value: Array<RiskOrder | RiskEvent>) {
        this.rawOrders = value;
    }

    setStringId(id: string) {
        this.stringId = id;
    }

    setConditions(value: ConditionType[]) {
        this.conditions = [...value];
        this.tablePage = 0;
        this.reapplyFilters();
    }

    /** 切换卡片 / 表格展示模式 */
    setViewMode(mode: RiskOrderListViewMode) {
        this.viewMode = mode;
        this.tablePage = 0;
    }

    /** 表格模式下切换异常订单 / 异常事件列表 */
    setTableListType(type: RiskOrderListTableType) {
        this.tableListType = type;
        this.tablePage = 0;
    }

    /** 切换即时风险数据模式并按新模式重新请求。 */
    setRiskDataMode(mode: RiskOrderMode) {
        this.riskDataMode = mode;
        if (mode === 'order') {
            this.tableListType = 'order';
        }
        this.tablePage = 0;
        void this.fetchData();
    }

    /**
     * 表格前端分页变更
     * @param page - 当前页（0-based）
     * @param perPage - 每页条数
     */
    changeTablePage(page: number, perPage: number) {
        this.tablePage = page;
        this.tablePerPage = perPage;
    }

    /** 筛选后的异常订单列表 */
    get filteredRiskOrders(): RiskOrder[] {
        return this.orders.filter(isRiskOrderListItemOrder) as RiskOrder[];
    }

    /** 筛选后的异常事件列表 */
    get filteredRiskEvents(): RiskEvent[] {
        return this.orders.filter((item) => !isRiskOrderListItemOrder(item)) as RiskEvent[];
    }

    /** 异常订单数量（当前筛选结果） */
    get abnormalOrderCount(): number {
        return this.filteredRiskOrders.length;
    }

    /** 异常事件数量（当前筛选结果） */
    get abnormalEventCount(): number {
        return this.filteredRiskEvents.length;
    }

    /** 异常订单 + 异常事件总数 */
    get totalAbnormalCount(): number {
        return this.abnormalOrderCount + this.abnormalEventCount;
    }

    /** 表格模式当前数据源（全量，分页由 TableComponent 前端切片） */
    get tableListData(): RiskOrder[] | RiskEvent[] {
        return this.tableListType === 'order' ? this.filteredRiskOrders : this.filteredRiskEvents;
    }

    /**
     * 设置日期范围并重新拉取即时风控列表（后端按 start_time / end_time 筛选）
     */
    setDateRange(start: Date, end: Date) {
        this.startTime = moment(start).format('YYYY-MM-DD HH:mm');
        this.endTime = moment(end).format('YYYY-MM-DD HH:mm');
        this.tablePage = 0;
        this.fetchData();
    }

    /** 按 rawOrders + 当前筛选条件刷新 orders */
    private reapplyFilters() {
        this.setOrders(this.applyFilters(this.rawOrders));
    }

    /** 读取某一筛选 key 的全部取值（多 Chip 为 OR） */
    private getConditionValues(key: string): string[] {
        return this.conditions
            .filter((item) => item.key === key && item.value)
            .map((item) => String(item.value));
    }

    /**
     * 文本字段模糊匹配：任一筛选词命中即通过（与云端 operator_name / table_name 行为一致）
     */
    private matchesTextField(fieldValue: string | number | undefined, filterValues: string[]): boolean {
        if (!filterValues.length) {
            return true;
        }
        const haystack = String(fieldValue ?? '').trim().toLowerCase();
        return filterValues.some((needle) => haystack.includes(String(needle).trim().toLowerCase()));
    }

    /**
     * 判断单条记录是否命中某一风险标签值（与云端 buildSearchParams 映射一致）
     */
    private itemMatchesRiskTagValue(item: RiskOrder | RiskEvent, tag: string): boolean {
        const isOrder = (item as RiskOrder).order_head_id !== undefined;
        if (isOrder) {
            const order = item as RiskOrder;
            const groupedTags = getRiskTagsForOrder(order as RiskOrderGroup);
            if (groupedTags.length > 0) {
                return groupedTags.some((candidate) =>
                    (candidate.type === 'risk' && tag === String(candidate.value)) ||
                    (candidate.type === 'event' && ((tag === '6' && candidate.value === 1) || (tag === '7' && candidate.value === 2))),
                );
            }
            if (tag === '1' || tag === '2' || tag === '3' || tag === '4' || tag === '5') {
                return String(order.risk_type ?? '') === tag;
            }
            if (tag === '6') {
                return order.event_type === 1;
            }
            if (tag === '7') {
                return order.event_type === 2;
            }
            return false;
        }
        const event = item as RiskEvent;
        if (tag === '6') {
            return event.eventType === 1;
        }
        if (tag === '7') {
            return event.eventType === 2;
        }
        return false;
    }

    /**
     * 按当前 conditions 在前端筛选（日期范围由接口 start_time / end_time 处理）
     */
    applyFilters(source: Array<RiskOrder | RiskEvent>): Array<RiskOrder | RiskEvent> {
        const riskTags = this.getConditionValues('risk_tag');
        const orderIdFilters = this.getConditionValues('order_head_id');
        const operatorFilters = this.getConditionValues('operator_name');
        const tableFilters = this.getConditionValues('table_name');

        return source.filter((item) => {
            if (riskTags.length > 0 && !riskTags.some((tag) => this.itemMatchesRiskTagValue(item, tag))) {
                return false;
            }

            const isOrder = (item as RiskOrder).order_head_id !== undefined;
            if (isOrder) {
                const order = item as RiskOrder;
                if (!this.matchesTextField(order.order_head_id, orderIdFilters)) {
                    return false;
                }
                if (!this.matchesTextField(order.operator_name, operatorFilters)) {
                    return false;
                }
                if (!this.matchesTextField(order.table_name, tableFilters)) {
                    return false;
                }
                return true;
            }

            const event = item as RiskEvent;
            const nestedOrders = event.orders ?? [];
            if (orderIdFilters.length > 0) {
                const orderHit = nestedOrders.some((o) => this.matchesTextField(o.order_head_id, orderIdFilters));
                if (!orderHit) {
                    return false;
                }
            }
            if (operatorFilters.length > 0) {
                const operatorHit = nestedOrders.some((o) => this.matchesTextField(o.operator_name, operatorFilters));
                if (!operatorHit) {
                    return false;
                }
            }
            if (tableFilters.length > 0) {
                const tableHit = nestedOrders.some((o) => this.matchesTextField(o.table_name, tableFilters));
                if (!tableHit) {
                    return false;
                }
            }
            return true;
        });
    }

    // ==================== 业务方法 ====================

    /**
     * 初始化状态，传入当前餐厅 stringId
     */
    initialize(stringId: string) {
        this.rawOrders = [];
        this.orders = [];
        this.stringId = stringId;
        this.conditions = [];
        this.viewMode = 'card';
        this.tableListType = 'order';
        this.riskDataMode = 'order';
        this.tablePage = 0;
        this.tablePerPage = 20;
        const { startTime, endTime } = validateCurrentDate();
        this.startTime = moment(startTime, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm');
        this.endTime = moment(endTime, 'YYYY-MM-DD HH:mm').format('YYYY-MM-DD HH:mm');
        this.closeDialogs();
        this.fetchData();
    }

    /**
     * 打开订单详情弹窗
     */
    openOrderDialog(order: RiskOrder) {
        this.selectedOrder = order;
        this.orderDialogOpen = true;
    }

    /**
     * 打开事件详情弹窗
     */
    openEventDialog(event: RiskEvent) {
        this.selectedEvent = {
            ...event,
            risk_status: event?.orders?.[0]?.event_status ?? 0
        };
        this.eventDialogOpen = true;
    }

    /**
     * 关闭所有弹窗
     */
    closeDialogs() {
        this.orderDialogOpen = false;
        this.eventDialogOpen = false;
        this.selectedOrder = null;
        this.selectedEvent = null;
    }

    /**
     * 关闭订单弹窗
     */
    closeOrderDialog() {
        this.orderDialogOpen = false;
        this.selectedOrder = null;
    }

    /**
     * 关闭事件弹窗
     */
    closeEventDialog() {
        this.eventDialogOpen = false;
        this.selectedEvent = null;
    }

    /**
     * 打开 AI 分析对话框。
     * 与菜品分析等页一致：先校验餐厅授权点 AU1030，未通过则提示无权限且不打开弹窗。
     */
    async openAiAnalysisDialog() {
        if (!this.stringId) {
            GlobalNotification.error(i18n.t('general_text_no_data'));
            return;
        }
        const warrantResponse = await apiManage.check_warrant_code({
            string_id: this.stringId,
            code: 'AU1030',
        });
        if (warrantResponse?.error || !warrantResponse.passedWarrant?.length) {
            GlobalNotification.error(i18n.t('restaurant_warrant_page_text_no_auth'));
            return;
        }
        this.aiAnalysisDialogOpen = true;
    }

    /** 关闭 AI 分析对话框 */
    closeAiAnalysisDialog() {
        this.aiAnalysisDialogOpen = false;
    }

    // ==================== 数据获取 ====================

    /**
     * 获取风险订单列表数据
     */
    async fetchData(stringId?: string) {
        if (stringId) {
            this.setStringId(stringId);
        }
        if (!this.stringId) return;

        this.setLoading(true);
        setLoading(true);

        const result = await apiManage.get_restaurant_riskOrder(this.stringId, {
            start_time: this.startTime,
            end_time: this.endTime,
            mode: this.riskDataMode,
        });

        if (result && !result.error) {
            const normalized = normalizeInstantRiskResponse(result as Record<string, unknown>, this.riskDataMode);
            const merged: Array<RiskOrder | RiskEvent> = this.riskDataMode === 'order'
                ? normalized.orders
                : [...normalized.riskOrders, ...normalized.riskEvents];
            this.setRawOrders(merged);
            this.setOrders(this.applyFilters(merged));
            this.tablePage = 0;
        } else {
            this.setRawOrders([]);
            this.setOrders([]);
            this.tablePage = 0;
        }

        this.setLoading(false);
        setLoading(false);
    }

    /**
     * 导出Excel文件
     * 包含两个sheet：风险订单和风险事件
     */
    downloadExcel() {
        // 分离风险订单和风险事件
        const riskOrders: RiskOrder[] = [];
        const riskEvents: RiskEvent[] = [];

        this.orders.forEach((item) => {
            if (isRiskOrderListItemOrder(item)) {
                riskOrders.push(item);
            } else {
                riskEvents.push(item);
            }
        });

        const sheetData: Array<{ sheetName?: string; data: string[][]; wpx: Array<{ wpx: number }> }> = [];

        // 1. 风险订单Sheet
        if (riskOrders.length > 0) {
            const riskOrderSheet = {
                sheetName: i18n.t('intelligent_risk_control_page_text_abnormal_orders'),
                data: [
                    [
                        i18n.t('business_analysis_page_order_account'),
                        i18n.t('business_analysis_page_table_name'),
                        i18n.t('immediate_number_people'),
                        i18n.t('intelligent_risk_control_page_column_dish_count'),
                        i18n.t('intelligent_risk_control_page_column_server'),
                        i18n.t('immediate_table_opening_time'),
                        i18n.t('immediate_statement_time'),
                        i18n.t('intelligent_risk_control_page_column_amount_original_actual'),
                        i18n.t('intelligent_risk_control_page_column_discount_amount'),
                        i18n.t('intelligent_risk_control_page_column_refund_count'),
                        i18n.t('intelligent_risk_control_page_column_refund_amount'),
                        i18n.t('intelligent_risk_control_page_column_avg_dish_count'),
                        i18n.t('intelligent_risk_control_page_column_duration_minutes'),
                        i18n.t('intelligent_risk_control_page_filter_risk_tag'),
                        i18n.t('restaurant_info_page_status'),
                    ]
                ],
                wpx: [
                    { wpx: 120 },
                    { wpx: 100 },
                    { wpx: 80 },
                    { wpx: 100 },
                    { wpx: 100 },
                    { wpx: 150 },
                    { wpx: 150 },
                    { wpx: 150 },
                    { wpx: 100 },
                    { wpx: 100 },
                    { wpx: 100 },
                    { wpx: 100 },
                    { wpx: 100 },
                    { wpx: 120 },
                    { wpx: 100 },
                    { wpx: 200 },
                ]
            };

            riskOrderSheet.data = riskOrderSheet.data.concat(
                riskOrders.map((order) => [
                    String(order.order_head_id || '-'),
                    order.table_name || '-',
                    String(order.people_count ?? '-'),
                    String(order.dish_count ?? '-'),
                    order.operator_name || '-',
                    order.open_time ? dateFormat(order.open_time, 'HH:mm DD/MM/YYYY') : '-',
                    order.close_time ? dateFormat(order.close_time, 'HH:mm DD/MM/YYYY') : '-',
                    `${formatRiskMoney(order.origin_amount)}/${formatRiskMoney(order.actual_amount)}`,
                    formatRiskMoney(order.discount_amount),
                    String(order.refund_dish_count ?? '-'),
                    formatRiskMoney(order.refund_amount),
                    String(order.avg_dish_per_person ?? '-'),
                    formatRiskMinutes(order.dining_duration_min),
                    getRiskTagTextForOrder(order as RiskOrderGroup),
                    i18n.t(formatRiskStatusText(order.orderStatus ?? order.risk_status ?? order.status)),
                ])
            );

            sheetData.push(riskOrderSheet);
        }

        // 2. 风险事件Sheet
        if (riskEvents.length > 0) {
            const riskEventSheet = {
                sheetName: i18n.t('intelligent_risk_control_page_text_abnormal_events'),
                data: [
                    [
                        i18n.t('intelligent_risk_control_page_filter_risk_tag'),
                        i18n.t('business_analysis_page_order_account'),
                        i18n.t('restaurant_extensions_page_text_create_time'),
                        i18n.t('restaurant_info_page_status'),
                    ]
                ],
                wpx: [
                    { wpx: 150 },
                    { wpx: 250 },
                    { wpx: 150 },
                    { wpx: 100 },
                ]
            };

            riskEventSheet.data = riskEventSheet.data.concat(
                riskEvents.map((event) => {
                    const orderIds = event.orders?.map(o => o.order_head_id).join(', ') || '-';

                    return [
                        i18n.t(formatEventTypeText(event.eventType)),
                        orderIds,
                        event.createdAt ? dateFormat(event.createdAt, 'HH:mm DD/MM/YYYY') : '-',
                        i18n.t(formatRiskStatusText(event.orders?.[0]?.event_status ?? 0)),
                    ];
                })
            );

            sheetData.push(riskEventSheet);
        }

        // 如果没有数据，不执行导出
        if (sheetData.length === 0) {
            GlobalNotification.error(i18n.t('general_text_no_data'));
            return;
        }

        // 生成文件名：餐厅名称 + 导出时间
        const DEFAULT_RESTAURANT = LSM.get(LOCAL_STORAGE_KEYS.DEFAULT_RESTAURANT) || {};
        const exportDateTime = dateFormat2(new Date().toISOString(), 'YYYY-MM-DD HH-mm');
        const fileName = `${i18n.t('intelligent_risk_control_page_text_restaurant_risk_control')} ${DEFAULT_RESTAURANT.nickname || ''} ${exportDateTime}`;

        // 导出Excel
        exportMultipleSheetExcel(fileName, sheetData);
    }

}

const riskOrderListState = new RiskOrderListState();

export default riskOrderListState;
